import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL as API_BASE } from '../config';

// Axios instance with auth token
const api = axios.create({
  baseURL: API_BASE,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    staff_id: null,
    is_superuser: false,
    is_active: true,
    role_ids: []
  });
  
  // Permission preview state
  const [showPermissionPreview, setShowPermissionPreview] = useState(false);
  const [previewPermissions, setPreviewPermissions] = useState([]);
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load users, roles, and available staff in parallel
      const [usersRes, rolesRes, staffRes] = await Promise.all([
        api.get('/api/users'),
        api.get('/api/users/roles'),
        api.get('/api/users/staff')
      ]);
      
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setAvailableStaff(staffRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (mode, user = null) => {
    setModalMode(mode);
    setSelectedUser(user);
    
    if (mode === 'edit' && user) {
      // Pre-fill form with user data
      setFormData({
        email: user.email,
        password: '',
        confirmPassword: '',
        staff_id: user.staff?.id || null,
        is_superuser: user.is_superuser,
        is_active: user.is_active,
        role_ids: roles.filter(r => user.roles.includes(r.name)).map(r => r.id)
      });
    } else {
      // Reset form for new user
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        staff_id: null,
        is_superuser: false,
        is_active: true,
        role_ids: []
      });
    }
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      staff_id: null,
      is_superuser: false,
      is_active: true,
      role_ids: []
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (modalMode === 'create' && !formData.email) {
      alert('Email is required');
      return;
    }
    
    if (modalMode === 'create' && !formData.password) {
      alert('Password is required');
      return;
    }
    
    if (formData.password && formData.password !== formData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    
    if (formData.password && formData.password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    
    try {
      if (modalMode === 'create') {
        // Create new user
        const payload = {
          email: formData.email,
          password: formData.password,
          staff_id: formData.staff_id,
          is_superuser: formData.is_superuser,
          is_active: formData.is_active,
          role_ids: formData.role_ids
        };
        
        await api.post('/api/users', payload);
        alert('User created successfully!');
      } else {
        // Update existing user
        const payload = {
          email: formData.email !== selectedUser.email ? formData.email : undefined,
          password: formData.password || undefined,
          staff_id: formData.staff_id,
          is_superuser: formData.is_superuser,
          is_active: formData.is_active
        };
        
        // Remove undefined fields
        Object.keys(payload).forEach(key => 
          payload[key] === undefined && delete payload[key]
        );
        
        await api.put(`/api/users/${selectedUser.id}`, payload);
        
        // Update roles separately
        await api.post(`/api/users/${selectedUser.id}/roles`, {
          role_ids: formData.role_ids
        });
        
        alert('User updated successfully!');
      }
      
      handleCloseModal();
      loadData();
    } catch (err) {
      console.error('Error saving user:', err);
      alert(err.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    
    try {
      await api.delete(`/api/users/${userToDelete.id}`);
      alert('User deleted successfully!');
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      loadData();
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleRole = (roleId) => {
    setFormData(prev => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter(id => id !== roleId)
        : [...prev.role_ids, roleId]
    }));
  };

  const handlePreviewPermissions = () => {
    // Aggregate all permissions from selected roles
    const selectedRoles = roles.filter(r => formData.role_ids.includes(r.id));
    const allPermissions = new Map();
    
    selectedRoles.forEach(role => {
      role.permissions.forEach(perm => {
        const key = `${perm.screen_key}:${perm.action}`;
        if (!allPermissions.has(key)) {
          allPermissions.set(key, {
            ...perm,
            roles: [role.name]
          });
        } else {
          allPermissions.get(key).roles.push(role.name);
        }
      });
    });
    
    setPreviewPermissions(Array.from(allPermissions.values()));
    setShowPermissionPreview(true);
  };

  // Group permissions by screen for better display
  const groupPermissionsByScreen = (permissions) => {
    const grouped = {};
    permissions.forEach(perm => {
      if (!grouped[perm.screen_key]) {
        grouped[perm.screen_key] = [];
      }
      grouped[perm.screen_key].push(perm);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="text-red-800 font-semibold">Error</div>
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">User Management</h1>
          <p className="text-gray-600 mt-1">Manage system users, roles, and permissions</p>
        </div>
        <button
          onClick={() => handleOpenModal('create')}
          className="bg-[#C7AE6E] hover:bg-[#B49A5F] text-white px-6 py-2 rounded-lg font-semibold transition-colors"
        >
          + Add New User
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Staff
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Roles
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.email}</div>
                      {user.is_superuser && (
                        <div className="text-xs text-red-600 font-semibold">SUPERUSER</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.staff ? (
                    <div>
                      <div className="text-sm text-gray-900">{user.staff.name}</div>
                      <div className="text-xs text-gray-500">{user.staff.role || user.staff.department}</div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">Not linked</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {user.roles.map((role) => (
                      <span
                        key={role}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {role}
                      </span>
                    ))}
                    {user.roles.length === 0 && (
                      <span className="text-sm text-gray-400">No roles</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.is_active ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Inactive
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleOpenModal('edit', user)}
                    className="text-[#C7AE6E] hover:text-[#9F8752] mr-3 font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setUserToDelete(user);
                      setShowDeleteConfirm(true);
                    }}
                    className="text-red-600 hover:text-red-800 font-medium"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {users.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No users found. Click "Add New User" to create one.
          </div>
        )}
      </div>

      {/* Create/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {modalMode === 'create' ? 'Create New User' : 'Edit User'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                {/* Email */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C7AE6E]"
                    required={modalMode === 'create'}
                  />
                </div>

                {/* Password */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password {modalMode === 'create' && '*'}
                    {modalMode === 'edit' && <span className="text-gray-500 text-xs ml-2">(leave blank to keep current)</span>}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C7AE6E]"
                    required={modalMode === 'create'}
                    minLength={6}
                  />
                </div>

                {/* Confirm Password */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password {modalMode === 'create' && '*'}
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C7AE6E]"
                    required={modalMode === 'create' || formData.password !== ''}
                  />
                </div>

                {/* Staff Link */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Link to Staff Member (Optional)
                  </label>
                  <select
                    value={formData.staff_id || ''}
                    onChange={(e) => setFormData({ ...formData, staff_id: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C7AE6E]"
                  >
                    <option value="">-- No staff link --</option>
                    {availableStaff.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.name} {staff.role && `(${staff.role})`}
                      </option>
                    ))}
                    {modalMode === 'edit' && selectedUser?.staff && (
                      <option value={selectedUser.staff.id}>
                        {selectedUser.staff.name} (Current)
                      </option>
                    )}
                  </select>
                </div>

                {/* Roles */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Assign Roles
                    </label>
                    {formData.role_ids.length > 0 && (
                      <button
                        type="button"
                        onClick={handlePreviewPermissions}
                        className="text-sm text-[#C7AE6E] hover:text-[#9F8752] font-medium"
                      >
                        Preview Permissions
                      </button>
                    )}
                  </div>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                    {roles.map((role) => (
                      <label key={role.id} className="flex items-start mb-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={formData.role_ids.includes(role.id)}
                          onChange={() => handleToggleRole(role.id)}
                          className="mt-1 mr-3"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-800">{role.name}</div>
                          {role.description && (
                            <div className="text-xs text-gray-500">{role.description}</div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            {role.permissions.length} permission(s)
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Checkboxes */}
                <div className="mb-6 space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="mr-3"
                    />
                    <span className="text-sm text-gray-700">Active (user can log in)</span>
                  </label>
                  
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_superuser}
                      onChange={(e) => setFormData({ ...formData, is_superuser: e.target.checked })}
                      className="mr-3"
                    />
                    <span className="text-sm text-gray-700 font-medium">
                      Superuser (bypasses all permission checks)
                    </span>
                  </label>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-[#C7AE6E] hover:bg-[#B49A5F] text-white rounded-lg font-semibold"
                  >
                    {modalMode === 'create' ? 'Create User' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Permission Preview Modal */}
      {showPermissionPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Permission Preview</h3>
                <button
                  onClick={() => setShowPermissionPreview(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>
              
              <div className="mb-4 text-sm text-gray-600">
                This user will have <strong>{previewPermissions.length}</strong> permission(s) from{' '}
                <strong>{formData.role_ids.length}</strong> role(s).
              </div>

              {Object.entries(groupPermissionsByScreen(previewPermissions)).map(([screen, perms]) => (
                <div key={screen} className="mb-4">
                  <h4 className="font-semibold text-gray-800 mb-2 capitalize">
                    {screen.replace(/_/g, ' ')}
                  </h4>
                  <div className="ml-4 space-y-1">
                    {perms.map((perm) => (
                      <div key={perm.id} className="flex items-center text-sm">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mr-2">
                          {perm.action}
                        </span>
                        <span className="text-gray-600">{perm.description || `${perm.action} access`}</span>
                        <span className="ml-auto text-xs text-gray-400">
                          {perm.roles.join(', ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete user <strong>{userToDelete.email}</strong>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setUserToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;

